/**
 * Composable useTransactionFilter — переиспользуемая логика загрузки и фильтрации транзакций
 *
 * Основные функции:
 * - Фильтрация по ID (универсальный поиск по ID транзакции и/или ID пользователя)
 * - Управление статусами оплаты (currentStatuses)
 * - Два режима: свободный поиск (AdminTransactions) и контекст пользователя (AdminUsers)
 */

// Реактивность и наблюдатели Vue
import { ref, watch } from 'vue'
// Стор администрирования — загрузка транзакций
import { useAdminStore } from '@/modules/admin/store/adminStore'
// Перечисление статусов оплаты — дефолтный фильтр "Завершена"
import { PaymentStatus } from '@vpn-tg/vue-client-grpc/proto/enums/transactionStatus'

/**
 * Опции composable
 *
 * @param fixedUserID — getter для фиксированного ID пользователя (контекст карточки юзера в AdminUsers).
 *   Если задан, фильтрация по ID работает только как поиск транзакции внутри этого пользователя (AND).
 *   Если не задан, фильтрация ищет параллельно по ID транзакции и ID пользователя (OR через два запроса).
 */
interface IUseTransactionFilterOptions {
  fixedUserID?: () => string | null
}

/**
 * Composable для загрузки и фильтрации транзакций
 *
 * @param options — опции composable (fixedUserID для контекста конкретного пользователя)
 * @returns filterID, currentStatuses, loadTransactions, onStatusChanged
 */
export function useTransactionFilter(options?: IUseTransactionFilterOptions) {
  // Стор администрирования
  const adminStore = useAdminStore()

  // Строка фильтрации по ID (транзакции или пользователя)
  const filterID = ref('')
  // Текущие выбранные статусы оплаты (дефолт — "Завершена")
  const currentStatuses = ref<PaymentStatus[]>([PaymentStatus.PaymentCompleted])

  /**
   * Загружает транзакции с текущими фильтрами
   *
   * @returns {Promise<boolean>} — успех операции
   */
  const loadTransactions = async (): Promise<boolean> => {
    // ID пользователя из контекста (AdminUsers)
    const fixedUserId = options?.fixedUserID?.()

    // Защита: если задан fixedUserID-getter, но пользователь не выбран — не делаем запрос
    if (options?.fixedUserID && !fixedUserId) return false

    // Значение поля фильтрации (clearable ставит null)
    const searchValue = filterID.value?.trim() ?? ''
    // Фильтр статусов для запроса
    const statusFilter =
      currentStatuses.value.length > 0 ? { paymentStatuses: currentStatuses.value } : {}

    if (fixedUserId) {
      // Контекст конкретного пользователя — AND-фильтрация: транзакции этого юзера + опционально по ID транзакции
      return await adminStore.getTransactions({
        userIDs: [fixedUserId],
        ...(searchValue ? { ids: [searchValue] } : {}),
        ...statusFilter,
      })
    }

    if (searchValue) {
      // Свободный поиск — параллельно ищем по ID транзакции и ID пользователя
      return await adminStore.searchTransactionsById(searchValue, currentStatuses.value)
    }

    // Без фильтра по ID — только статусы
    return await adminStore.getTransactions(statusFilter)
  }

  /**
   * Обработчик изменения фильтра статусов — перезагружает транзакции
   *
   * @param {PaymentStatus[]} statuses — выбранные статусы оплаты
   */
  const onStatusChanged = async (statuses: PaymentStatus[]) => {
    currentStatuses.value = statuses
    await loadTransactions()
  }

  // Таймер debounce для отложенного поиска по ID
  let searchTimeout: ReturnType<typeof setTimeout>

  // Автоматический поиск при изменении filterID с debounce 500ms
  watch(filterID, (newValue) => {
    clearTimeout(searchTimeout)
    // clearable ставит null — приводим к пустой строке
    const value = newValue?.trim() ?? ''
    if (!value) {
      // При очистке поля — сразу перезагружаем без фильтра
      loadTransactions()
      return
    }
    searchTimeout = setTimeout(() => {
      loadTransactions()
    }, 500)
  })

  return {
    /** Строка фильтрации по ID (v-model для поля ввода) */
    filterID,
    /** Текущие выбранные статусы оплаты */
    currentStatuses,
    /** Загрузка транзакций с текущими фильтрами */
    loadTransactions,
    /** Обработчик изменения статусов */
    onStatusChanged,
  }
}
